import { Injectable } from '@angular/core';
import {
  AccessUserDetailDto,
  AccessUserDetail,
  SiteOption,
  AccessUserListDto,
  AccessUser,
  AccessUserCpRolePayload,
  AccessUserSiteDto,
  BackofficeUserDetailDto,
  BackofficeUserDetail,
  BackofficeUserLoginLogDto,
  BackofficeUserLoginLog,
  BackofficeUserSessionDto,
  BackofficeUserSession,
  BackofficeUserPayload,
  BackofficePasswordMode,
} from '../../models/system-settings/access-users.model';

@Injectable({
  providedIn: 'root',
})
export class MapperAccessUsersService {

  constructor() { }

  mapAccessUserFromDto(dto: AccessUserListDto): AccessUser {
    const raw = dto as AccessUserListDto & Record<string, unknown>;
    const sitesRaw = (raw.sites ?? raw['Sites'] ?? []) as AccessUserSiteDto[];
    return {
      id: Number(raw.id ?? raw['Id'] ?? 0),
      login: String(raw.login ?? raw['Login'] ?? ''),
      displayName: String(raw.displayName ?? raw['DisplayName'] ?? ''),
      active: !!(raw.active ?? raw['Active'] ?? false),
      createDate: (raw.createDate ?? raw['CreateDate'])
        ? new Date(String(raw.createDate ?? raw['CreateDate']))
        : null,
      siteIdStr: String(raw.siteIdStr ?? raw['SiteIdStr'] ?? ''),
      cpRole: Number(raw.cpRole ?? raw['CpRole'] ?? 0),
      roleName: String(raw.roleName ?? raw['RoleName'] ?? ''),
      stampAllowed: !!(raw.stampAllowed ?? raw['StampAllowed'] ?? false),
      stampRanges: !!(raw.stampRanges ?? raw['StampRanges'] ?? false),
      stampMany: !!(raw.stampMany ?? raw['StampMany'] ?? false),
      cityLoginEnabled: raw.cityLoginEnabled !== false && raw['CityLoginEnabled'] !== false,
      hasCityPassword: !!(raw.hasCityPassword ?? raw['HasCityPassword'] ?? false),
      mustChangePassword: !!(raw.mustChangePassword ?? raw['MustChangePassword'] ?? false),
      sites: (sitesRaw || []).map((s) => ({
        id: Number((s as any).id ?? (s as any).Id ?? 0),
        name: String((s as any).name ?? (s as any).Name ?? ''),
      })),
      siteTotalCount: Number(raw.siteTotalCount ?? raw['SiteTotalCount'] ?? sitesRaw?.length ?? 0),
    };
  }

  mapAccessUserToCpRolePayload(
    form: {
      cpRole: number;
      displayName: string;
      siteIdStr: string;
      stampAllowed: boolean;
      stampRanges: boolean;
      stampMany: boolean;
    },
    userId: number,
    menuId: number,
  ): AccessUserCpRolePayload {
    return {
      cpRole: form.cpRole,
      userId,
      menuId,
      displayName: form.displayName.trim(),
      siteIdStr: form.siteIdStr,
      stampAllowed: form.stampAllowed,
      stampRanges: form.stampRanges,
      stampMany: form.stampMany,
    };
  }

  mapSiteOption(dto: { id: number; name: string }): SiteOption {
    const raw = dto as { id?: number; Id?: number; name?: string; Name?: string };
    return {
      id: Number(raw.id ?? raw.Id ?? 0),
      name: String(raw.name ?? raw.Name ?? ''),
    };
  }

  mapAccessUserDetail(dto: AccessUserDetailDto): AccessUserDetail {
    return {
      id: dto.id,
      active: dto.active ?? false,
      login: dto.login ?? '',
      createDate: dto.createDate ? new Date(dto.createDate) : null,
      writeDate: dto.writeDate ? new Date(dto.writeDate) : null,
      odooSystem: dto.odooSystem ?? false,
      stampPassword: dto.stampPassword ?? null,
      cpRole: dto.cpRole ?? 0,
      roleName: dto.roleName ?? '',
      siteIdStr: dto.siteIdStr ?? '',
      sites: (dto.sites ?? []).map((s) => this.mapSiteOption(s)),
    };
  }

  mapSitesFromDto(dto: AccessUserDetailDto): SiteOption[] {
    return (dto?.sites ?? []).map((s) => this.mapSiteOption(s));
  }

  mapBackofficeUserDetail(dto: BackofficeUserDetailDto): BackofficeUserDetail {
    const raw = dto as BackofficeUserDetailDto & Record<string, unknown>;
    const sitesRaw = (raw.sites ?? raw['Sites'] ?? []) as AccessUserSiteDto[];
    const writeDate = raw.cityPasswordWriteDate ?? raw['CityPasswordWriteDate'];
    const createDate = raw.createDate ?? raw['CreateDate'];
    const maxSessions = raw.maxConcurrentSessions ?? raw['MaxConcurrentSessions'];
    return {
      id: Number(raw.id ?? raw['Id'] ?? 0),
      partnerId: Number(raw.partnerId ?? raw['PartnerId'] ?? 0),
      login: String(raw.login ?? raw['Login'] ?? ''),
      displayName: String(raw.displayName ?? raw['DisplayName'] ?? ''),
      email: String(raw.email ?? raw['Email'] ?? ''),
      phone: String(raw.phone ?? raw['Phone'] ?? ''),
      active: !!(raw.active ?? raw['Active'] ?? false),
      cityLoginEnabled: raw.cityLoginEnabled !== false && raw['CityLoginEnabled'] !== false,
      hasCityPassword: !!(raw.hasCityPassword ?? raw['HasCityPassword'] ?? false),
      mustChangePassword: !!(raw.mustChangePassword ?? raw['MustChangePassword'] ?? false),
      maxConcurrentSessions: maxSessions == null || Number(maxSessions) < 1 || Number.isNaN(Number(maxSessions))
        ? null
        : Number(maxSessions),
      cityPasswordWriteDate: writeDate ? new Date(String(writeDate)) : null,
      cpRole: Number(raw.cpRole ?? raw['CpRole'] ?? 0),
      roleName: String(raw.roleName ?? raw['RoleName'] ?? ''),
      siteIdStr: String(raw.siteIdStr ?? raw['SiteIdStr'] ?? ''),
      stampAllowed: !!(raw.stampAllowed ?? raw['StampAllowed'] ?? false),
      stampRanges: !!(raw.stampRanges ?? raw['StampRanges'] ?? false),
      stampMany: !!(raw.stampMany ?? raw['StampMany'] ?? false),
      createDate: createDate ? new Date(String(createDate)) : null,
      sites: (sitesRaw || []).map((s) => this.mapSiteOption(s as { id: number; name: string })),
    };
  }

  mapBackofficeUserPayload(
    form: {
      login: string;
      displayName: string;
      email: string;
      phone: string;
      cpRole: number;
      siteIdStr: string;
      stampAllowed: boolean;
      stampRanges: boolean;
      stampMany: boolean;
      active: boolean;
      cityLoginEnabled: boolean;
      maxConcurrentSessions: number | null;
      passwordMode?: BackofficePasswordMode;
      password?: string;
      mustChangePassword?: boolean;
    },
    userId: number,
    menuId: number,
    includePassword: boolean,
  ): BackofficeUserPayload {
    const payload: BackofficeUserPayload = {
      userId,
      menuId,
      login: form.login.trim(),
      displayName: form.displayName.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      cpRole: form.cpRole,
      siteIdStr: form.siteIdStr,
      stampAllowed: form.stampAllowed,
      stampRanges: form.stampRanges,
      stampMany: form.stampMany,
      active: form.active,
      cityLoginEnabled: form.cityLoginEnabled,
      maxConcurrentSessions: form.maxConcurrentSessions,
    };
    if (includePassword) {
      payload.passwordMode = form.passwordMode ?? 'self_set';
      payload.password = form.passwordMode === 'admin_set' ? (form.password || null) : null;
      payload.mustChangePassword = !!form.mustChangePassword;
    }
    return payload;
  }

  mapLoginLogFromDto(dto: BackofficeUserLoginLogDto): BackofficeUserLoginLog {
    const raw = dto as BackofficeUserLoginLogDto & Record<string, unknown>;
    const createDate = raw.createDate ?? raw['CreateDate'];
    return {
      id: Number(raw.id ?? raw['Id'] ?? 0),
      userId: raw.userId == null && raw['UserId'] == null ? null : Number(raw.userId ?? raw['UserId']),
      login: String(raw.login ?? raw['Login'] ?? ''),
      eventType: String(raw.eventType ?? raw['EventType'] ?? ''),
      ipAddress: String(raw.ipAddress ?? raw['IpAddress'] ?? ''),
      userAgent: String(raw.userAgent ?? raw['UserAgent'] ?? ''),
      message: String(raw.message ?? raw['Message'] ?? ''),
      createDate: createDate ? new Date(String(createDate)) : null,
    };
  }

  mapUserSessionFromDto(dto: BackofficeUserSessionDto): BackofficeUserSession {
    const raw = dto as BackofficeUserSessionDto & Record<string, unknown>;
    const loginAt = raw.loginAt ?? raw['LoginAt'];
    const lastSeenAt = raw.lastSeenAt ?? raw['LastSeenAt'];
    return {
      id: Number(raw.id ?? raw['Id'] ?? 0),
      userId: Number(raw.userId ?? raw['UserId'] ?? 0),
      ipAddress: String(raw.ipAddress ?? raw['IpAddress'] ?? ''),
      userAgent: String(raw.userAgent ?? raw['UserAgent'] ?? ''),
      loginAt: loginAt ? new Date(String(loginAt)) : null,
      lastSeenAt: lastSeenAt ? new Date(String(lastSeenAt)) : null,
      isActive: !!(raw.isActive ?? raw['IsActive'] ?? false),
    };
  }
}
